import { Form, Formik, type FormikHelpers } from "formik";
import {
  type EmailReceiverParams,
  type FTPImporterParams,
  type GuruSystemsCSVIntegrationParams,
  type GuruSystemsIntegrationParams,
  type SFTPReceiverParams,
  assetTypeEnum,
} from "kubb";

// TODO: Remove once kubb types are regenerated from backend OpenAPI spec
interface Switch2CMi6110Params {
  integration_type?: "switch2_cmi6110";
  api_key: string;
  user_id: string;
  site_id?: string | null;
  device_list_cron?: string;
  data_polling_cron?: string;
}
import type * as React from "react";
import * as Yup from "yup";
import { ErrorBanner } from "../../../components/Error/ErrorBanner";
import NewTextFormComponent from "../../../components/Forms/NewTextFormComponent";
import { SelectComboField } from "../../../components/Forms/SelectCombo/SelectCombo";
import { yupRequiredString } from "../../../components/Forms/yupValidators";
import BlockSpinner from "../../../components/Spinners/BlockSpinner";
import { Button } from "../../../components/Theme/button";
import {
  useCreateAssetPosition,
  useCreateLocation,
} from "../../../hooks/createEntity";
import DataProcessingConfigurationForm from "../assetPositions/DataProcessingConfigurationForm";
import EmailReceiverIntegrationForm from "./EmailReceiverIntegrationForm";
import GuruApiIntegrationForm from "./GuruAPIIntegrationForm";
import SftpReceiverIntegrationForm, {
  NONE_PARSER_TYPE,
} from "./SFTPReceiverIntegrationForm";
import Switch2CMI6110IntegrationForm from "./Switch2CMI6110IntegrationForm";

type Props = {
  company_id: string;
  scheme_id: string;
  postCreateActions?: () => void;
};

interface DataProcessingFormValues {
  autoCreateNewAssets: string;
}

interface FormValues {
  address: string;
  integration:
    | GuruSystemsIntegrationParams
    | GuruSystemsCSVIntegrationParams
    | FTPImporterParams
    | SFTPReceiverParams
    | EmailReceiverParams
    | Switch2CMi6110Params;
  dataProcessing: DataProcessingFormValues;
}

export function IntegrationCreateForm(props: Props) {
  const createLocation = useCreateLocation(props.company_id);
  const createAssetPosition = useCreateAssetPosition(props.company_id);

  const submit = (
    values: FormValues,
    { setSubmitting, resetForm }: FormikHelpers<FormValues>,
  ): void => {
    setSubmitting(true);
    createLocation
      .update({
        scheme_id: props.scheme_id,
        company_id: props.company_id,
        address: values.address,
      })
      .then(([location]) => {
        // Transform SFTP receiver parser_type: convert sentinel to null
        const integration = { ...values.integration };
        if (
          "parser_type" in integration &&
          (integration as { parser_type?: string }).parser_type ===
            NONE_PARSER_TYPE
        ) {
          (integration as SFTPReceiverParams).parser_type = null;
        }

        return createAssetPosition.update({
          scheme_id: props.scheme_id,
          company_id: props.company_id,
          location_id: location.location_id,
          asset_position_type: assetTypeEnum.api_integration,
          configuration: {
            integration: integration,
            data_processing: {
              auto_create_new_assets:
                values.dataProcessing?.autoCreateNewAssets !== "false",
            },
          },
        });
      })
      .then(() => {
        resetForm();
        if (props.postCreateActions) {
          props.postCreateActions();
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const initialValues: FormValues = {
    address: "",
    integration: {
      integration_type: "guru_systems",
      api_key: "",
      site_id: 0,
    },
    dataProcessing: {
      autoCreateNewAssets: "true",
    },
  };
  const creating = createLocation.loading || createAssetPosition.loading;

  function configurationDetails(integrationType: string): React.ReactElement {
    switch (integrationType) {
      case "guru_systems":
        return (
          <>
            <GuruApiIntegrationForm namespace={"integration"} />
            <DataProcessingConfigurationForm namespace={"dataProcessing"} />
          </>
        );
      case "sftp_receiver":
        return <SftpReceiverIntegrationForm namespace={"integration"} />;
      case "guru_systems_csv":
        return <SftpReceiverIntegrationForm namespace={"integration"} />;
      case "email_receiver":
        return <EmailReceiverIntegrationForm namespace={"integration"} />;
      case "switch2_cmi6110":
        return (
          <>
            <Switch2CMI6110IntegrationForm namespace={"integration"} />
            <DataProcessingConfigurationForm namespace={"dataProcessing"} />
          </>
        );
      default:
        return <></>;
    }
  }

  function defaultConfigurationValues(
    integrationType: string,
  ):
    | GuruSystemsIntegrationParams
    | GuruSystemsCSVIntegrationParams
    | FTPImporterParams
    | SFTPReceiverParams
    | EmailReceiverParams
    | Switch2CMi6110Params {
    switch (integrationType) {
      case "guru_systems":
        return {
          integration_type: "guru_systems",
          api_key: "",
          site_id: 0,
        } as GuruSystemsIntegrationParams;
      case "sftp_receiver":
        return {
          integration_type: "sftp_receiver",
          parser_type: NONE_PARSER_TYPE,
          sftp_credentials: { type: "public_key", public_key: "" },
        } as unknown as SFTPReceiverParams;
      case "guru_systems_csv":
        return {
          integration_type: "guru_systems_csv",
          sftp_credentials: { type: "public_key", public_key: "" },
        } as GuruSystemsCSVIntegrationParams;
      case "email_receiver":
        return {
          integration_type: "email_receiver",
          processing: { quarantine_all: true },
        } as EmailReceiverParams;
      case "switch2_cmi6110":
        return {
          integration_type: "switch2_cmi6110",
          api_key: "",
          user_id: "",
          site_id: null,
          device_list_cron: "0 * * * ? *",
          data_polling_cron: "*/15 * * * ? *",
        } as Switch2CMi6110Params;
      default:
        return {};
    }
  }

  return (
    <Formik
      onSubmit={submit}
      initialValues={initialValues}
      enableReinitialize={true}
      validateOnMount={true}
      validationSchema={Yup.object().shape({
        address: yupRequiredString,
      })}
    >
      {({
        values,
        setFieldValue,
        isValid,
        isSubmitting,
      }): React.JSX.Element => (
        <BlockSpinner loading={creating}>
          <Form data-cy={"integration-create"}>
            <h3 className="text-white mb-0">Add Data Integration</h3>
            <NewTextFormComponent
              fieldName={"address"}
              label={{ label: "Description" }}
            />
            <SelectComboField
              fieldName={"integration.integration_type"}
              label={{ label: "Integration Type" }}
              isMulti={false}
              options={[
                { value: "guru_systems", label: "Guru Systems (API)" },
                { value: "guru_systems_csv", label: "Guru Systems (CSV)" },
                { value: "sftp_receiver", label: "SFTP Receiver" },
                { value: "ftp_importer", label: "FTP Importer" },
                { value: "email_receiver", label: "Email Receiver" },
                { value: "switch2_cmi6110", label: "Switch2 CMi6110" },
              ]}
              onChange={(v) => {
                setFieldValue(
                  "integration",
                  defaultConfigurationValues(v as string),
                );
              }}
            />
            {configurationDetails(
              values.integration.integration_type as string,
            )}
            <Button
              type={"submit"}
              color={"brandLight"}
              disabled={isSubmitting || !isValid}
            >
              Add Data Integration
            </Button>
            <ErrorBanner
              error={createLocation.error || createAssetPosition.error}
            />
          </Form>
        </BlockSpinner>
      )}
    </Formik>
  );
}
